import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import $document from '../../../tools/document';

function renderShimmerLoader(contentRows, id) {
  const loaderRows = [];
  for (let row = 1; row <= contentRows; row += 1) {
    loaderRows.push(
      <div className="left-rail-row" key={`shimmer-loader-row-${row}`}>
        <div className="image" />
        <div className="content" />
      </div>,
    );
  }

  return (
    <div className="shimmer-loader" data-testid={id}>
      <div className="left-rail">
        <div className="first-row" />
        <div className="second-row" />
        {loaderRows}
      </div>
      <div className="payment-rail" />
    </div>
  );
}

function renderLoader(rows) {
  const loaderRows = [];
  for (let row = 1; row <= rows; row += 1) {
    loaderRows.push(
      <span key={`loader-row-${row}`} />,
    );
  }

  return loaderRows;
}

export default function Loader({
  variant,
  classList,
  contentRows,
  children,
  isActive,
  id,
}) {
  let innerContent;
  if (variant === 'dots') {
    innerContent = renderLoader(2);
  } else if (variant === 'spinner') {
    innerContent = renderLoader(6);
  } else if (variant === 'shimmer') {
    return renderShimmerLoader(contentRows, id);
  }

  const loaderContent = variant === 'hazy' ? (
    <span data-testid={id} className={`hazy-loader ${classList}`} data-variant={variant} data-state={isActive}>
      <span className="screen-reader-text">loading...</span>
      {children}
    </span>
  ) : (
    <span data-testid={id} className={`loader ${classList}`} data-variant={variant}>
      {innerContent}
      <span className="screen-reader-text">loading...</span>
    </span>
  );
  return loaderContent;
}

// Named export for LoaderPortal
export function LoaderPortal({
  contentRows = 2,
  targetSelector = '.page-wrapper',
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const targetElement = $document?.querySelector(targetSelector);

  // targetElement will be null during the final render of SSR and
  // available on the first render of CSR, because the document object
  // is not present in SSR but is in CSR. To prevent hydration errors,
  // we render null on the initial CSR render and then render the loader
  // once the target is available.
  if (!targetElement || !mounted) {
    return null;
  }

  return createPortal(
    <div className="purx-loader-overlay">
      <div className="purx-loader-content">
        <Loader variant="shimmer" contentRows={contentRows} />
      </div>
    </div>,
    targetElement,
  );
}

Loader.defaultProps = {
  variant: 'spinner',
  classList: '',
  contentRows: 1,
  children: '',
  isActive: false,
  id: '',
};

Loader.propTypes = {
  variant: PropTypes.oneOf(['spinner', 'hazy', 'dots', 'shimmer']),
  classList: PropTypes.string,
  contentRows: PropTypes.number,
  children: PropTypes.node,
  isActive: PropTypes.bool,
  id: PropTypes.string,
};

LoaderPortal.propTypes = {
  contentRows: PropTypes.number,
  targetSelector: PropTypes.string,
};

LoaderPortal.defaultProps = {
  contentRows: 2,
  targetSelector: '.page-wrapper',
};
